import { useCallback, useRef, useState } from 'react';

export interface Busy<K> {
  /** Keys with a request in flight (drives the disabled button). */
  busy: ReadonlySet<K>;
  /** Run `fn` for `key` unless one is already running; a second tap is a no-op. */
  run: (key: K, fn: () => Promise<void>) => Promise<void>;
}

/**
 * Per-key re-entry guard for one-shot requests, so a double tap can't create
 * two tasks or send two revokes. The ref blocks synchronously (two taps in one
 * frame both see it); the state only re-renders the button.
 */
export function useBusy<K>(): Busy<K> {
  const inFlight = useRef(new Set<K>());
  const [busy, setBusy] = useState<ReadonlySet<K>>(new Set());

  const run = useCallback(async (key: K, fn: () => Promise<void>): Promise<void> => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setBusy(new Set(inFlight.current));
    try {
      await fn();
    } finally {
      inFlight.current.delete(key);
      setBusy(new Set(inFlight.current));
    }
  }, []);

  return { busy, run };
}
